import React, { useState, useRef, useEffect } from 'react';
import { Geo, SearchForSuggestionsResult, Place } from '@aws-amplify/geo';
import { TextField, Box } from '@mui/material';

interface AddressAutoCompleteProps {
	startingValue?: string;
	skipLengthCheck?: boolean;
	returnPostcode?: boolean;
	label?: string;
	error?: boolean;
	sx?: any;
	size?: 'small' | 'medium';
	widthProp?: string;
	onSelectAddress: (place: Place) => void;
}

const AddressAutoComplete: React.FC<AddressAutoCompleteProps> = ({
	startingValue,
	skipLengthCheck,
	returnPostcode,
	label = 'Enter Postcode',
	sx,
	size = 'medium',
	error,
	widthProp,
	onSelectAddress
}) => {
	const [query, setQuery] = useState(startingValue ? startingValue : '');
	const [suggestions, setSuggestions] = useState<SearchForSuggestionsResult[]>([]);
	const suggestionsBoxRef = useRef<HTMLDivElement>(null);

	const handleSearch = async (event: React.ChangeEvent<HTMLInputElement>) => {
		const inputValue = event.target.value;
		setQuery(inputValue);

		if (skipLengthCheck || inputValue.length > 2) {
			try {
				const results = await Geo.searchForSuggestions(inputValue, { maxResults: 5 });
				setSuggestions(results);
			} catch (error) {
				console.error('Error searching for suggestions:', error);
			}
		} else {
			setSuggestions([]);
		}
	};

	const handleSuggestionClick = async (suggestion: SearchForSuggestionsResult) => {
		if (!returnPostcode) {
			setQuery(suggestion.text);
		}
		setSuggestions([]);

		try {
			const detailedPlace = await Geo.searchByText(suggestion.text);
			if (detailedPlace && detailedPlace.length > 0) {
				if (returnPostcode) {
					setQuery(detailedPlace[0].postalCode || '');
				}
				onSelectAddress(detailedPlace[0]);
			}
		} catch (error) {
			console.error('Error fetching detailed place information:', error);
		}
	};

	const handleClickOutside = (event: MouseEvent) => {
		if (suggestionsBoxRef.current && !suggestionsBoxRef.current.contains(event.target as Node)) {
			setSuggestions([]);
		}
	};

	useEffect(() => {
		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);

	return (
		<>
			<Box sx={{ position: 'relative' }} ref={suggestionsBoxRef} width={widthProp}>
				<TextField
					id="outlined-basic"
					label={label}
					variant="outlined"
					error={error}
					size={size}
					value={query}
					onChange={handleSearch}
					fullWidth
					autoComplete="off"
					{...sx}
				/>
				{suggestions.length > 0 && (
					<ul
						className="suggestions-list"
						style={{
							listStyleType: 'none',
							paddingLeft: 0,
							position: 'absolute',
							zIndex: 9998,
							backgroundColor: 'background.paper',
							width: '100%',
							boxShadow: '0px 4px 6px rgba(0,0,0,0.1)'
						}}
					>
						{suggestions.map((suggestion, index) => (
							<li
								key={index}
								onClick={() => handleSuggestionClick(suggestion)}
								style={{ cursor: 'pointer', padding: '8px', borderBottom: '1px solid #ccc' }}
							>
								{suggestion.text}
							</li>
						))}
					</ul>
				)}
			</Box>
		</>
	);
};

export default AddressAutoComplete;
